/**
 * EntityBadgeByEmail — hydration wrapper that resolves an email address to a
 * CRM entity and renders an EntityBadge.
 *
 * Resolved as user/contact: renders the entity badge; clicking opens the
 *   slideout (or navigates to the record page when `clickBehavior='page'`).
 * Unresolved: renders the email as the display name; clicking opens the
 *   "Create contact" modal pre-filled with just the email.
 *
 * Do NOT pass a `data` prop — use `EntityBadge` directly if you already
 * have the resolved data.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { CONTACT } from '@/lib/crm/core/entityTypes';
import { useOpenCreateContact } from '@/lib/crm/hooks/useOpenCreateContact';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import { usePersonByEmail } from '@/lib/crm/hooks/usePersonByEmail';
import { entityTypeToRouteSlug } from '@/lib/crm/utils/entityTypeUtils';

import { EntityBadge } from './EntityBadge';
import { HoverPreviewBadge } from './HoverPreviewBadge';
import type {
  ClickBehavior,
  EntityBadgeData,
  EntityBadgeVariant,
} from './types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface EntityBadgeByEmailProps {
  email: string;
  orgId: number;
  variant?: EntityBadgeVariant;
  avatar?: boolean;
  interactive?: boolean;
  disabled?: boolean;
  /** How a resolved badge should navigate on click. Defaults to `'slideout'`. */
  clickBehavior?: ClickBehavior;
  /**
   * When supplied, overrides `clickBehavior` — the badge calls this
   * directly instead of performing default navigation.
   */
  onClick?: () => void;
  /** Additional className applied to the underlying badge. */
  className?: string;
  /**
   * When true, the underlying `EntityBadge` renders the email in parentheses
   * after the display name (e.g. `Nicola (nicola@balio.com)`). Pass-through to
   * the primitive — see `EntityBadge#showEmail`. Defaults to false.
   */
  showEmail?: boolean;
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EntityBadgeByEmail({
  email,
  orgId,
  variant = 'default',
  avatar = false,
  interactive = true,
  disabled = false,
  clickBehavior = 'slideout',
  onClick,
  className,
  showEmail = false,
}: EntityBadgeByEmailProps): React.ReactElement {
  const { person, isLoading } = usePersonByEmail(email, String(orgId));
  const openSlideout = useOpenSlideoutOptional();
  const navigate = useNavigate();
  // Canonical Create Contact opener — config (entityType,
  // createProperties / createAssociations, submit handler) lives in
  // `useOpenCreateContact`. The unresolved-email path (this file)
  // delegates to it so the create-contact flow stays in one place.
  const { openCreateContact } = useOpenCreateContact(orgId);

  // Click handler for a resolved user/contact. Only used when no `onClick`
  // override is supplied (see `resolvedOnClick` below).
  const handleResolvedClick = useCallback(() => {
    if (!person) {
      return;
    }
    if (clickBehavior === 'slideout') {
      openSlideout?.(person.entityId);
    } else if (clickBehavior === 'page') {
      navigate(
        `/${orgId}/crm/${entityTypeToRouteSlug(person.type)}/${person.entityId}`
      );
    }
  }, [person, clickBehavior, openSlideout, navigate, orgId]);

  // Click handler for an unresolved email — opens the Create Contact form
  // pre-filled with only the email (no entity to read name properties from).
  const handleUnresolvedClick = useCallback(() => {
    if (clickBehavior === 'none') {
      return;
    }
    openCreateContact({ ahoy_email: [email] });
  }, [clickBehavior, email, openCreateContact]);

  // --- Loading state ---
  if (isLoading) {
    return (
      <EntityBadge
        state={{ kind: 'loading' }}
        variant={variant}
        className={className}
      />
    );
  }

  // --- Resolved state ---
  if (person) {
    const data: EntityBadgeData = {
      id: person.entityId,
      entityType: person.type,
      displayName: person.name,
      email,
    };
    // All resolved chips (user/contact) route through the standard
    // slideout/page navigation.
    const resolvedOnClick =
      onClick ?? (clickBehavior !== 'none' ? handleResolvedClick : undefined);
    return (
      <HoverPreviewBadge
        entityId={person.entityId}
        orgId={orgId}
        entityType={person.type}
        state={{ kind: 'data', data }}
        variant={variant}
        avatar={avatar}
        interactive={interactive}
        disabled={disabled}
        onClick={resolvedOnClick}
        className={className}
        showEmail={showEmail}
      />
    );
  }

  // --- Unresolved: email becomes click-to-create affordance ---
  const unresolvedData: EntityBadgeData = {
    id: `unresolved-${email}`,
    entityType: CONTACT,
    displayName: email,
    email,
  };
  const unresolvedOnClick =
    onClick ?? (clickBehavior !== 'none' ? handleUnresolvedClick : undefined);
  return (
    <EntityBadge
      state={{ kind: 'data', data: unresolvedData }}
      variant={variant}
      avatar={avatar}
      interactive={interactive}
      disabled={disabled}
      onClick={unresolvedOnClick}
      className={className}
      showEmail={showEmail}
    />
  );
}
